'use client';

import { SUBSPLASH_AUTH_PROVIDER_ID, useShellContext } from '@omni/kit';
import {
  KitButton,
  KitInternationalPhoneNumberInput,
  KitLink,
  KitLoader,
  KitText,
} from '@omni/kit/components';
import KitAppIcon from '@omni/kit/components/KitAppIcon';
import KitCopyrightLogo from '@omni/kit/components/KitCopyrightLogo';
import KitDividerWithText from '@omni/kit/components/KitDividerWithText';
import { Country } from '@omni/kit/components/KitInternationalPhoneNumberInput/constants/types';
import Show from '@omni/kit/components/Show';
import { SUBSPLASH_PHONE_AUTH_PROVIDER_ID } from '@omni/kit/constants/identifiers';
import { SizeClassV2, useSizeClass } from '@omni/kit/contexts/SizeClassContext';
import { useSourceContext } from '@omni/kit/contexts/SourceContext';
import { useFeatureFlag } from '@omni/kit/hooks/useFeatureFlag';
import { useSupportedCountries } from '@omni/kit/hooks/useSupportedCountries';
import { useTranslation } from '@omni/kit/hooks/useTranslation';
import Colors from '@omni/kit/theming/Colors';
import Spacing from '@omni/kit/theming/Spacing';
import React, { useState } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import GetAppHeaders from '../Downloader/GetAppHeaders';
import Environment, { IS_LOCALHOST } from '../Environment';
import HealthMonitor from '../HealthMonitor';
import useShellAuthProviders from '../contexts/ShellContext/hooks/useShellAuthProviders';
import { UNLEASH_TOGGLES } from '../services/UnleashService';
import BorderRadius from '../theming/BorderRadius';
import {
  capitalizeFirstLetter,
  handleErrorResponse,
} from '../utilities/utilities';
import { LoginButtons } from './LoginButtons';
import VerifyCodeScreen from './VerifyCodeScreen';
import { AuthProvider } from './index';

const debug = require('debug')('tca:auth:AuthProviderList');

interface AuthProviderListProps {
  appKey: string;
  customReturnUrl?: string;
  customHeader?: JSX.Element;
  appBrandingUrl?: string;
  openInNewTab?: boolean;
  orgKey?: string;
  targetAuthProviderId: string;
  showFooter?: boolean;
}

export default function AuthProviderList({
  appKey,
  customReturnUrl,
  customHeader,
  appBrandingUrl,
  openInNewTab,
  targetAuthProviderId,
  showFooter = true,
}: AuthProviderListProps): JSX.Element {
  const [verifyCodeScreenVisible, setVerifyCodeScreenVisible] = useState(false);
  const [authUrl, setAuthUrl] = useState('');
  const [confirmUrl, setConfirmUrl] = useState('');
  const [phoneAuthState, setPhoneAuthState] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [callingCode, setCallingCode] = useState('');
  const [regionCode, setRegionCode] = useState('');

  const { sizeClassV2 } = useSizeClass();
  const isMobileDevice = sizeClassV2 < SizeClassV2.M;

  const insets = useSafeAreaInsets();

  const source = useSourceContext();

  const { isFetchingAuthProviders, loginEnabledProviders, providers } =
    useShellAuthProviders();

  const { appUrls } = useShellContext();
  const loginWithPhoneSupportedCountriesUrl =
    Platform.OS === 'web'
      ? `${Environment.feedsHost}/api/v1/login-with-phone-supported-countries`
      : appUrls?.loginWithPhoneSupportedCountriesUrl;

  const targetProvider = providers.find(
    (c) => c.authproviderid === targetAuthProviderId
  );

  const hasPhoneAuthProviderEnabled = loginEnabledProviders.some(
    (provider) => provider.authproviderid === SUBSPLASH_PHONE_AUTH_PROVIDER_ID
  );

  // Enterprise apps with custom login providers will not show the Subsplash branding (e.g. Ligonier and Wild at Heart)
  const suppressSubsplashBranding =
    loginEnabledProviders.length > 0 &&
    providers.find(
      (authProvider) =>
        authProvider.authproviderid === SUBSPLASH_AUTH_PROVIDER_ID &&
        !authProvider.enable_login
    );

  const containerStyle = [
    styles.container,
    isMobileDevice
      ? {
          maxWidth: 455,
        }
      : {
          maxWidth: 420,
        },
  ];

  return (
    <View
      style={{
        alignItems: 'center',
        backgroundColor: Colors.N0,
        justifyContent: 'center',
        flexGrow: 1, // needed to fill the remaining space
        paddingTop: Spacing.xl,
      }}
    >
      {verifyCodeScreenVisible ? (
        <View style={containerStyle}>
          <VerifyCodeScreen
            authUrl={authUrl}
            confirmUrl={confirmUrl}
            phoneAuthState={phoneAuthState}
            phoneNumber={phoneNumber}
            callingCode={callingCode}
            regionCode={regionCode}
            setPhoneNumber={setPhoneNumber}
            setVerifyCodeScreenVisible={setVerifyCodeScreenVisible}
            customReturnUrl={customReturnUrl}
            source={source}
            targetProvider={targetProvider}
          />
        </View>
      ) : isFetchingAuthProviders ? (
        <View style={styles.loadingStyle}>
          <KitLoader />
        </View>
      ) : (
        <View style={containerStyle}>
          <View
            style={[
              styles.authProviderContainer,
              suppressSubsplashBranding && { marginBottom: 0 },
              isMobileDevice && { maxWidth: 350 },
            ]}
          >
            {customHeader || (
              <LoginHeader
                appBrandingUrl={appBrandingUrl}
                hasPhoneAuthProviderEnabled={hasPhoneAuthProviderEnabled}
              />
            )}
            {hasPhoneAuthProviderEnabled ? (
              <LoginWithPhone
                loginEnabledProviders={loginEnabledProviders}
                loginWithPhoneSupportedCountriesUrl={
                  loginWithPhoneSupportedCountriesUrl
                }
                phoneNumber={phoneNumber}
                setAuthUrl={setAuthUrl}
                setConfirmUrl={setConfirmUrl}
                setPhoneAuthState={setPhoneAuthState}
                setPhoneNumber={setPhoneNumber}
                setCallingCode={setCallingCode}
                setRegionCode={setRegionCode}
                setVerifyCodeScreenVisible={setVerifyCodeScreenVisible}
              />
            ) : null}
            <View style={styles.authProvider}>
              <LoginButtons
                appKey={appKey}
                customReturnUrl={customReturnUrl}
                loginEnabledProviders={loginEnabledProviders || []}
                openInNewTab={openInNewTab}
                source={source}
                targetProvider={targetProvider}
              />
              {/* eslint-disable i18next/no-literal-string */}
              {!suppressSubsplashBranding ? (
                <KitText
                  center
                  fontSize={14}
                  style={{ marginTop: 6, paddingHorizontal: Spacing.xs }}
                >
                  By continuing you agree to Subsplash's{' '}
                  <KitLink
                    href='https://www.subsplash.com/legal/terms-of-use'
                    fontSize={14}
                  >
                    Terms of Use
                  </KitLink>{' '}
                  and{' '}
                  <KitLink
                    href='https://www.subsplash.com/legal/privacy'
                    fontSize={14}
                  >
                    Privacy Policy
                  </KitLink>
                  .
                </KitText>
              ) : null}
              {/* eslint-enable i18next/no-literal-string */}
            </View>
          </View>
          {showFooter && !suppressSubsplashBranding ? (
            <KitCopyrightLogo
              viewStyle={{ marginBottom: insets.bottom + Spacing.xl }}
            />
          ) : null}
        </View>
      )}
    </View>
  );
}

const LoginHeader = ({
  appBrandingUrl,
  hasPhoneAuthProviderEnabled,
}: {
  appBrandingUrl?: string;
  hasPhoneAuthProviderEnabled: boolean;
}) => {
  const { t } = useTranslation(['auth']);

  const { app } = useShellContext();

  return (
    <>
      <KitAppIcon
        size={64}
        imageUrl={appBrandingUrl || app?.branding?.mobileIcon}
      />
      <KitText center h1 style={{ marginTop: Spacing.m }}>
        {hasPhoneAuthProviderEnabled
          ? t('auth:loginToContinue')
          : t('auth:loginHeaderTitle')}
      </KitText>
    </>
  );
};

const LoginWithPhone = ({
  loginEnabledProviders,
  loginWithPhoneSupportedCountriesUrl,
  phoneNumber,
  setAuthUrl,
  setConfirmUrl,
  setPhoneAuthState,
  setPhoneNumber,
  setCallingCode,
  setRegionCode,
  setVerifyCodeScreenVisible,
}: {
  loginEnabledProviders: AuthProvider[];
  loginWithPhoneSupportedCountriesUrl?: string;
  phoneNumber: string;
  setAuthUrl: (url: string) => void;
  setConfirmUrl: (url: string) => void;
  setPhoneAuthState: (state: string) => void;
  setPhoneNumber: (number: string) => void;
  setCallingCode: (code: string) => void;
  setRegionCode: (code: string) => void;
  setVerifyCodeScreenVisible: (visible: boolean) => void;
}) => {
  const authUrl = loginEnabledProviders.find(
    (provider) => provider.authproviderid === SUBSPLASH_PHONE_AUTH_PROVIDER_ID
  )?.auth_url;
  const { t } = useTranslation(['auth']);

  const { supportedCountries, firstSupportedCountry, isLoadingCountries } =
    useSupportedCountries(loginWithPhoneSupportedCountriesUrl);

  const { tokens } = useShellContext();

  const [isLoading, setIsLoading] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<Country | undefined>(
    firstSupportedCountry
  );
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleCountryChange = (country: Country) => {
    setSelectedCountry(country);
  };

  const isValidPhoneNumber = (phoneNumber: string) => {
    const significant = phoneNumber.replace(/\D/g, '');

    return significant.length === selectedCountry?.length;
  };

  const authorizePhoneNumber = async () => {
    const appHeaders = await GetAppHeaders();

    if (authUrl) {
      try {
        setIsLoading(true);
        setShowError(false);
        setErrorMessage('');

        const response = await fetch(authUrl, {
          method: 'POST',
          body: JSON.stringify({
            phone_number: {
              significant: phoneNumber.replace(/\D/g, ''),
              country: {
                region_code: selectedCountry?.regionCode,
                calling_code: selectedCountry?.callingCode,
              },
            },
            ...(Platform.OS === 'web' && {
              skip_deep_link: true,
              use_refresh_cookie: !IS_LOCALHOST,
            }),
          }),
          headers: {
            ...appHeaders,
            Authorization: `Bearer ${tokens.guest}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const responseData = await response.json();
          const { confirm_url: confirmUrl, state: phoneAuthState } =
            responseData;

          setAuthUrl(authUrl);
          setConfirmUrl(confirmUrl);
          setPhoneAuthState(phoneAuthState);
          setPhoneNumber(phoneNumber);
          setCallingCode(selectedCountry?.callingCode ?? '');
          setRegionCode(selectedCountry?.regionCode ?? '');
          setVerifyCodeScreenVisible(true);
        } else {
          const { status, message, data } = await handleErrorResponse(response);
          const invalidPhoneNumberError =
            status === 400 && message?.includes('invalid phone number');

          if (status !== 0 && status !== 429 && !invalidPhoneNumberError) {
            HealthMonitor.logError(data, {
              tags: {
                label: 'auth:failed to authorize phone',
              },
            });
          }

          const errorMessage = invalidPhoneNumberError
            ? t('auth:error.enterValidPhoneNumber')
            : capitalizeFirstLetter(message);

          setErrorMessage(errorMessage);
          setShowError(true);
        }
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <>
      <KitInternationalPhoneNumberInput
        label={t('profile:textMobilePhoneNumber')}
        subtext={t('auth:wellTextYouToConfirmYourNumber')}
        countries={supportedCountries}
        selectedCountry={selectedCountry}
        onCountryChange={(country) => {
          handleCountryChange(country);
          setShowError(false);
        }}
        value={phoneNumber}
        onChangeText={(text) => {
          setPhoneNumber(text);
          setShowError(false);
        }}
        returnKeyType='go'
        blurOnSubmit
        onSubmitEditing={() => {
          if (
            isValidPhoneNumber(phoneNumber) &&
            !isLoading &&
            !isLoadingCountries
          ) {
            authorizePhoneNumber();
          }
        }}
        style={styles.phoneInputContainer}
      />
      <Show show={showError}>
        <View style={styles.errorContainer}>
          <KitText color={Colors.N0} fontSize={16}>
            {errorMessage}
          </KitText>
        </View>
      </Show>
      <KitButton
        title={t('common:buttonTitleContinue')}
        onPress={authorizePhoneNumber}
        isLoading={isLoading}
        color={Colors.N900}
        style={styles.phoneButton}
        disabled={
          !isValidPhoneNumber(phoneNumber) || isLoading || isLoadingCountries
        }
      />
      <KitDividerWithText text={t('common:or')} />
    </>
  );
};

const styles = StyleSheet.create({
  appleWarningBox: {
    alignItems: 'center',
    backgroundColor: Colors.N50,
    borderRadius: 6,
    flexDirection: 'row',
    marginTop: Spacing.xxl,
    maxWidth: 423,
    padding: Spacing.l,
  },
  authProvider: {
    marginTop: Spacing.xl,
    width: '100%',
  },
  authProviderContainer: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    marginBottom: Spacing.xxl,
    paddingHorizontal: Spacing.s,
    width: '100%',
  },
  container: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    width: '100%',
  },
  brandingContainer: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
  },
  loadingStyle: {
    alignItems: 'center',
    minHeight: 200,
    justifyContent: 'center',
  },
  phoneButton: {
    width: '100%',
    marginBottom: Spacing.xl,
  },
  phoneInputContainer: {
    width: '100%',
    marginTop: Spacing.xl,
    marginBottom: Spacing.xl,
  },
  errorContainer: {
    paddingHorizontal: Spacing.l,
    paddingVertical: Spacing.m,
    backgroundColor: Colors.R500,
    borderRadius: BorderRadius.m,
    marginBottom: Spacing.l,
    width: '100%',
  },
});
